import { useTranslation } from "react-i18next";
import { memo, useCallback } from "react";
import {
  TAppointmentArrivalConfigForm,
  appointmentArrivalConfigSchema,
} from "./appointmentArrivalConfigSchema";
import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { IAppointmentArrivalConfigProps } from "./models";
import {
  createAppointmentArrivalConfig,
  getAppointmentArrivalConfigById,
  updateAppointmentArrivalConfigById,
} from "../../../../../../../services/appointmentArrivalConfig.service";
import { Box, Grid, Typography } from "@mui/material";
import { TimeInput } from "../../../../../../../components/TimeInput";

const convertDateToTimeString = (date: Date) => {
  return date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const convertTimeStringToDate = (timeString: string) => {
  const date = new Date();
  const getTime = timeString.split(":");
  date.setHours(parseInt(getTime[0]));
  date.setMinutes(parseInt(getTime[1]));
  date.setSeconds(parseInt(getTime[2]));

  return date;
};

const translationPath =
  "chatBot.page.service.appointmentArrivalRule.appointmentArrivalConfig.";

export const AppointmentArrivalConfig = memo(
  ({
    id,
    appointmentArrivalConfigId,
    onBackButton,
  }: IAppointmentArrivalConfigProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createAppointmentArrivalConfig,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateAppointmentArrivalConfigById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getAppointmentArrivalConfigById,
    );

    const saveHandler = (v: TAppointmentArrivalConfigForm) =>
      save({
        ...v,
        appointmentArrivalRuleId: appointmentArrivalConfigId ?? "",
        timeStart: convertDateToTimeString(v.timeStart),
        timeEnd: convertDateToTimeString(v.timeEnd),
        arrivalTime: convertDateToTimeString(v.arrivalTime),
      });

    const updateHandler = (v: TAppointmentArrivalConfigForm, id: string) =>
      update({
        item: {
          ...v,
          appointmentArrivalRuleId: appointmentArrivalConfigId ?? "",
          timeStart: convertDateToTimeString(v.timeStart),
          timeEnd: convertDateToTimeString(v.timeEnd),
          arrivalTime: convertDateToTimeString(v.arrivalTime),
        },
        id: id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TAppointmentArrivalConfigForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const timeStart = convertTimeStringToDate(data.timeStart);
          const timeEnd = convertTimeStringToDate(data.timeEnd);
          const arrivalTime = convertTimeStringToDate(data.arrivalTime);
          const newData: TAppointmentArrivalConfigForm = {
            ...data,
            timeStart,
            timeEnd,
            arrivalTime,
          };
          return { data: newData, success: true };
        }

        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TAppointmentArrivalConfigForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "appointment_arrival_config")}
        schema={appointmentArrivalConfigSchema}
        subtitle={t(translationPath + "new_appointment_arrival_config")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Box sx={{ pl: 2 }}>
            <Grid
              container
              spacing={2}
              sx={(t) => ({
                pr: 3,
                pl: 1,
                py: 3,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 2,
              })}
            >
              <Grid item sm={12}>
                <Typography fontWeight="bold">
                  {t(translationPath + "if")}
                </Typography>
              </Grid>

              <Grid item sm={12}>
                <Typography>{t(translationPath + "appointments")}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TimeInput
                  name="timeStart"
                  label={t(translationPath + "from")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TimeInput name="timeEnd" label={t(translationPath + "to")} />
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              sx={(t) => ({
                mt: 5,
                pr: 3,
                pl: 1,
                py: 3,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 2,
              })}
            >
              <Grid item sm={12}>
                <Typography fontWeight="bold">
                  {t(translationPath + "then")}
                </Typography>
              </Grid>

              <Grid item sm={12}>
                <Typography>
                  {t(translationPath + "arrival_time_description")}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TimeInput
                  name="arrivalTime"
                  label={t(translationPath + "arrival_time")}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      />
    );
  },
);
